import { css } from '@emotion/react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { darkTheme } from '@/theme/dark-theme';
import { spacing } from '@/theme/tokens';

const HIDE_DELAY_MS = 3000;

export interface TeleprompterControlsProps {
  currentSection: number;
  sectionCount: number;
  volume: number;
  onVolumeChange: (v: number) => void;
  onSettingsOpen: () => void;
}

const iconButtonCss = css`
  background: none;
  border: none;
  color: ${darkTheme.textSecondary};
  cursor: pointer;
  min-width: 44px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  padding: ${spacing.xs};

  &:hover {
    color: ${darkTheme.text};
    background: ${darkTheme.surface};
  }
`;

export function TeleprompterControls({
  currentSection,
  sectionCount,
  volume,
  onVolumeChange,
  onSettingsOpen,
}: TeleprompterControlsProps) {
  const [visible, setVisible] = useState(true);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const resetTimer = useCallback(() => {
    setVisible(true);
    /* v8 ignore next 3 -- timerRef is always set by the initial useEffect before any event handler calls resetTimer */
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(
      /* v8 ignore next -- timer callback tested via fake timers but flaky under coverage instrumentation */
      () => setVisible(false),
      HIDE_DELAY_MS,
    );
  }, []);

  useEffect(() => {
    timerRef.current = setTimeout(() => {
      setVisible(false);
    }, HIDE_DELAY_MS);

    const handleActivity = () => resetTimer();
    document.addEventListener('mousemove', handleActivity);
    document.addEventListener('touchstart', handleActivity);

    return () => {
      /* v8 ignore next 3 -- cleanup always has a timer ref since it's set on mount */
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      document.removeEventListener('mousemove', handleActivity);
      document.removeEventListener('touchstart', handleActivity);
    };
  }, [resetTimer]);

  const progress = sectionCount > 0 ? ((currentSection + 1) / sectionCount) * 100 : 0;

  return (
    <div
      data-testid="teleprompter-controls"
      css={css`
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        display: flex;
        flex-direction: column;
        gap: ${spacing.sm};
        padding: ${spacing.xl} ${spacing.md} ${spacing.md};
        background: linear-gradient(
          transparent 0%,
          ${darkTheme.background}80 30%,
          ${darkTheme.background} 50%
        );
        opacity: ${visible ? 1 : 0};
        pointer-events: ${visible ? 'auto' : 'none'};
        transition: opacity 0.3s ease;
        z-index: 10;
      `}
    >
      {/* Section indicator + progress bar */}
      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: 6px;
        `}
      >
        <span
          css={css`
            color: ${darkTheme.textSecondary};
            font-size: 13px;
          `}
        >
          Section {currentSection + 1} of {sectionCount}
        </span>
        <div
          role="progressbar"
          aria-valuenow={currentSection + 1}
          aria-valuemin={0}
          aria-valuemax={sectionCount}
          aria-label="Section progress"
          css={css`
            height: 3px;
            border-radius: 2px;
            background: ${darkTheme.border};
            overflow: hidden;
          `}
        >
          <div
            css={css`
              height: 100%;
              width: ${progress}%;
              background: ${darkTheme.primary};
              transition: width 0.3s ease;
            `}
          />
        </div>
      </div>

      {/* Following indicator */}
      <div
        css={css`
          display: flex;
          justify-content: center;
          align-items: center;
          padding: ${spacing.sm} 0;
        `}
      >
        <span
          css={css`
            color: ${darkTheme.textSecondary};
            font-size: 14px;
            font-weight: 500;
          `}
        >
          Following
        </span>
      </div>

      {/* Utility row */}
      <div
        css={css`
          display: flex;
          align-items: center;
          justify-content: space-between;
        `}
      >
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 4px;
          `}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill={darkTheme.textSecondary}
            aria-hidden="true"
          >
            <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
          </svg>
          <input
            type="range"
            min={0}
            max={100}
            value={volume}
            aria-label="Volume"
            onChange={(e) => onVolumeChange(Number(e.target.value))}
            css={css`
              width: 80px;
              accent-color: ${darkTheme.text};
            `}
          />
        </div>

        <button
          type="button"
          onClick={onSettingsOpen}
          aria-label="Open settings"
          css={iconButtonCss}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94L14.4 2.81c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41L9.25 5.35c-.59.24-1.13.56-1.62.94L5.24 5.33c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.22-.07.47.12.61l2.03 1.58c-.05.3-.07.63-.07.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z" />
          </svg>
        </button>
      </div>
    </div>
  );
}
